import type { Cell, Style } from 'exceljs';

import type {
  BorderSide,
  BorderStyle,
  CellStyle,
} from '../../domain/report-style.types';

const BORDER_COLOR_ARGB = 'FFCCCCCC';

export function applyCellStyle(
  cell: Cell,
  style: Partial<CellStyle>,
): void {
  applyFont(cell, style);
  applyFill(cell, style);
  applyAlignment(cell, style);
  applyBorder(cell, style);
}

export function styleToExcelJsStyle(
  style: Partial<CellStyle>,
): Partial<Style> {
  return {
    font: buildConditionalFont(style),
    fill: buildConditionalFill(style),
  };
}

function hasFontFlag(style: Partial<CellStyle>): boolean {
  return Boolean(
    style.bold ||
      style.italic ||
      style.underline ||
      style.fontColor ||
      style.fontSize ||
      style.fontName,
  );
}

function applyFont(cell: Cell, style: Partial<CellStyle>): void {
  if (!hasFontFlag(style)) return;

  cell.font = {
    bold: style.bold,
    italic: style.italic,
    underline: style.underline,
    size: style.fontSize,
    name: style.fontName,
    color: style.fontColor ? { argb: style.fontColor } : undefined,
  };
}

function applyFill(cell: Cell, style: Partial<CellStyle>): void {
  if (!style.fgColor) return;

  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: style.fgColor },
  };
}

function applyAlignment(cell: Cell, style: Partial<CellStyle>): void {
  if (!style.align && !style.verticalAlign && !style.wrapText) {
    return;
  }

  cell.alignment = {
    horizontal: style.align,
    vertical: style.verticalAlign,
    wrapText: style.wrapText,
  };
}

type SolidBorderStyle = Exclude<BorderStyle, 'none'>;

function borderSide(
  borderStyle: SolidBorderStyle,
  argb: string,
): { style: SolidBorderStyle; color: { argb: string } } {
  return { style: borderStyle, color: { argb } };
}

function resolveSide(
  side: BorderSide | undefined,
  defaultColor: string,
): { style: SolidBorderStyle; color: { argb: string } } | undefined {
  if (!side || side.style === 'none') return undefined;
  return borderSide(side.style, side.color ?? defaultColor);
}

// Control explícito por lado: cada lado se dibuja solo si viene en el
// objeto. Tiene prioridad sobre el modelo uniforme `border`.
function applyPerSideBorders(cell: Cell, style: Partial<CellStyle>): void {
  const color = style.borderColor ?? BORDER_COLOR_ARGB;
  const sides = style.borders!;
  cell.border = {
    top: resolveSide(sides.top, color),
    left: resolveSide(sides.left, color),
    right: resolveSide(sides.right, color),
    bottom: resolveSide(sides.bottom, color),
  };
}

function applyBorder(cell: Cell, style: Partial<CellStyle>): void {
  if (style.borders) {
    applyPerSideBorders(cell, style);
    return;
  }

  const baseColor = style.borderColor ?? BORDER_COLOR_ARGB;
  const base =
    style.border && style.border !== 'none'
      ? borderSide(style.border, baseColor)
      : undefined;

  const bottom =
    style.bottomBorder && style.bottomBorder.style !== 'none'
      ? borderSide(
          style.bottomBorder.style,
          style.bottomBorder.color ?? baseColor,
        )
      : base;

  if (!base && !bottom) return;

  cell.border = { top: base, left: base, right: base, bottom };
}

function buildConditionalFont(
  style: Partial<CellStyle>,
): Partial<Style>['font'] {
  if (!style.bold && !style.italic && !style.fontColor) {
    return undefined;
  }
  return {
    bold: style.bold,
    italic: style.italic,
    color: style.fontColor ? { argb: style.fontColor } : undefined,
  };
}

function buildConditionalFill(
  style: Partial<CellStyle>,
): Partial<Style>['fill'] {
  if (!style.fgColor) return undefined;
  return {
    type: 'pattern',
    pattern: 'solid',
    bgColor: { argb: style.fgColor },
  };
}
